import React, { useState, useEffect, useMemo } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { useDataProvider, useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { useCourseEnrollment } from '../../hooks/useCourseEnrollment';
import { displayEnrollmentMode } from '../../utils/enrollmentMode';
import { RatingDisplay } from '../Ratings';
import { ratingService, RatingSummary } from '../../services/ratingService';
import MaterialSymbol from '../common/MaterialSymbol';
import { buildCatalogCourseFilter } from './CourseCatalog.courseFilter';
import './course-catalog-design.css';
import { AvailableInChips } from '../Courses/AvailableInChips';

interface Course {
    id: number;
    title: string;
    description?: string;
    thumbnailUrl?: string;
    estimatedDuration?: number;
    difficulty: string;
    isActive: boolean;
    enrollmentMode: string;
    lessons?: any[];
    isLicensedReference?: boolean;
    licenseId?: number;
    catalogId?: number | null;
    catalogName?: string | null;
}

interface Enrollment {
    id: number;
    course: any;
    status: string;
    progressPercentage: number;
    lastAccessedAt?: string;
}

interface CourseCatalogInfo {
    id: number;
    name: string;
}

// Bucketing-Helper aus eigener Datei importieren — die ist react-admin-frei
// und damit per Jest direkt testbar. Re-Export fuer existierende Imports
// (z.B. in andere Komponenten falls vorhanden).
import {
    LICENSED_KEY,
    NO_CATALOG_KEY,
    pickCourseBucket,
} from './CourseCatalog.bucketing';
export { LICENSED_KEY, NO_CATALOG_KEY, pickCourseBucket };

const getCatalogId = (catalog: any): number | null => {
    if (!catalog) return null;
    if (typeof catalog === 'number') return catalog;
    if (typeof catalog === 'object' && catalog.id !== undefined) return Number(catalog.id);
    if (typeof catalog === 'string') {
        const parts = catalog.split('/');
        const last = parts[parts.length - 1];
        const n = parseInt(last);
        return isNaN(n) ? null : n;
    }
    return null;
};

const getCatalogName = (catalog: any): string | null => {
    if (catalog && typeof catalog === 'object' && typeof catalog.name === 'string') {
        return catalog.name;
    }
    return null;
};

const getDifficultyLabel = (d: string) => {
    switch (d) {
        case 'beginner': return 'Einstieg';
        case 'intermediate': return 'Vertiefung';
        case 'advanced': return 'Spezialisierung';
        default: return d;
    }
};

const difficultyOrder: Record<string, number> = { beginner: 0, intermediate: 1, advanced: 2 };

/**
 * Anzeige-Status eines Katalog-Kurses. Wording an die Kursübersicht
 * ("Meine Kurse") angeglichen (Kunden-Feedback): "In Bearbeitung",
 * "Noch nicht begonnen", "Abgeschlossen".
 */
type CatalogStatus = 'in_progress' | 'not_started' | 'completed' | 'pending' | 'available';

const getCatalogStatus = (enrollment?: Enrollment): CatalogStatus => {
    if (!enrollment) return 'available';
    switch (enrollment.status) {
        case 'completed': return 'completed';
        case 'pending_approval': return 'pending';
        case 'enrolled': return 'not_started';
        // 'in_progress', 'failed' u. ä.: Kurs wurde begonnen
        default: return 'in_progress';
    }
};

const STATUS_OPTIONS = [
    { value: 'in_progress', label: 'In Bearbeitung' },
    { value: 'not_started', label: 'Noch nicht begonnen' },
    { value: 'completed', label: 'Abgeschlossen' },
];

const ENROLLMENT_OPTIONS = [
    { value: 'enrolled', label: 'Eingeschrieben' },
    { value: 'not_enrolled', label: 'Nicht eingeschrieben' },
];

type FilterGroupKey = 'catalog' | 'level' | 'status' | 'enrollment';
type Filters = Record<FilterGroupKey, string[]>;
type SortKey = 'name' | 'duration' | 'level';

const EMPTY_FILTERS: Filters = { catalog: [], level: [], status: [], enrollment: [] };

interface CatalogCardData {
    course: Course;
    enrollment?: Enrollment;
    bucket: { key: number | string; name: string };
    status: CatalogStatus;
    ratingSummary?: RatingSummary;
    dueDate?: string;
    note?: string;
}

interface FilterOption {
    value: string;
    label: string;
}

interface FilterGroupProps {
    title: string;
    options: FilterOption[];
    selected: string[];
    onToggle: (value: string) => void;
}

const FilterGroup: React.FC<FilterGroupProps> = ({ title, options, selected, onToggle }) => (
    <div className="fil-group">
        <h4>{title}</h4>
        {options.map((o) => (
            <label className="fil" key={o.value}>
                <input
                    type="checkbox"
                    checked={selected.includes(o.value)}
                    onChange={() => onToggle(o.value)}
                />
                <span className="box"><MaterialSymbol name="check" /></span>
                {o.label}
            </label>
        ))}
    </div>
);

interface CatalogCardProps {
    data: CatalogCardData;
    onEnroll: (course: Course) => void;
    enrolling: boolean;
    onContinue: (courseId: number) => void;
}

const CatalogCard: React.FC<CatalogCardProps> = ({ data, onEnroll, enrolling, onContinue }) => {
    const { course, enrollment, bucket, status, ratingSummary, dueDate, note } = data;
    const dueDateObj = dueDate ? new Date(dueDate) : null;
    const diffDays = dueDateObj
        ? Math.ceil((dueDateObj.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
        : null;
    const dueClass =
        diffDays === null ? 'due-ok' :
        diffDays <= 3 ? 'due-soon' :
        diffDays <= 14 ? 'due-near' : 'due-ok';

    const renderStatus = () => {
        if (status === 'in_progress') {
            const p = enrollment?.progressPercentage || 0;
            return (
                <>
                    <div className="cstatus">Fortschritt: {p}%</div>
                    <div className="cbar"><i style={{ width: `${Math.max(p, 2)}%` }} /></div>
                </>
            );
        }
        if (status === 'not_started') {
            return <div className="cstatus"><MaterialSymbol name="schedule" />Noch nicht begonnen</div>;
        }
        if (status === 'completed') {
            return <div className="cstatus done"><MaterialSymbol name="task_alt" />Abgeschlossen</div>;
        }
        if (status === 'available') {
            return <div className="cstatus">Nicht eingeschrieben</div>;
        }
        return null; // pending — Chip im Aktionsbereich
    };

    const renderAction = () => {
        if (status === 'pending') {
            return (
                <span className="kstatus-chip warn">
                    <MaterialSymbol name="hourglass_top" />
                    Anfrage ausstehend
                </span>
            );
        }
        if (enrollment) {
            if (status === 'completed') {
                return (
                    <button type="button" className="cbtn outline" onClick={() => onContinue(course.id)}>
                        <MaterialSymbol name="play_arrow" filled />
                        Wiederholen
                    </button>
                );
            }
            return (
                <button type="button" className="cbtn solid" onClick={() => onContinue(course.id)}>
                    <MaterialSymbol name="play_arrow" filled />
                    {status === 'not_started' ? 'Kurs starten' : 'Weiter lernen'}
                </button>
            );
        }
        const enrollmentMode = displayEnrollmentMode(course.enrollmentMode);
        if (enrollmentMode === 'self') {
            return (
                <button
                    type="button"
                    className="cbtn solid"
                    onClick={() => onEnroll(course)}
                    disabled={enrolling}
                >
                    <MaterialSymbol name="school" />
                    {enrolling ? 'Einschreiben...' : 'Einschreiben'}
                </button>
            );
        }
        if (enrollmentMode === 'request') {
            return (
                <button
                    type="button"
                    className="cbtn outline"
                    onClick={() => onEnroll(course)}
                    disabled={enrolling}
                >
                    <MaterialSymbol name="hourglass_top" />
                    {enrolling ? 'Senden...' : 'Einschreibung anfragen'}
                </button>
            );
        }
        return <p className="kcontact">Kontaktieren Sie Ihre Ansprechpartner</p>;
    };

    return (
        <article className="cat2-card">
            <div className="lmain">
                <span className="ctag"><MaterialSymbol name="menu_book" />{bucket.name}</span>
                <h3>{course.title}</h3>
                <AvailableInChips courseId={course.id} compact />
                {course.description && (
                    <p className="kdesc">
                        {course.description.length > 120
                            ? `${course.description.substring(0, 120)}...`
                            : course.description}
                    </p>
                )}
                {note && (
                    <div className="knote">
                        <MaterialSymbol name="info" />
                        <span><strong>Hinweis:</strong> {note}</span>
                    </div>
                )}
                <div className="krow">
                    <span className="lvl">{getDifficultyLabel(course.difficulty)}</span>
                    {course.estimatedDuration ? (
                        <span className="dur">
                            <MaterialSymbol name="schedule" />
                            {course.estimatedDuration} Min.
                        </span>
                    ) : null}
                    {dueDateObj && (
                        <span className={`kdue ${dueClass}`}>
                            <MaterialSymbol name="event" />
                            Fällig: {dueDateObj.toLocaleDateString('de-DE')}
                        </span>
                    )}
                </div>
                {ratingSummary && ratingSummary.ratingCount > 0 && (
                    <div className="krating">
                        <RatingDisplay
                            averageRating={ratingSummary.averageRating}
                            ratingCount={ratingSummary.ratingCount}
                            size="small"
                        />
                    </div>
                )}
                {renderStatus()}
            </div>
            <div className="act">{renderAction()}</div>
        </article>
    );
};

const CourseCatalog: React.FC = () => {
    const [courses, setCourses] = useState<Course[]>([]);
    const [enrollments, setEnrollments] = useState<Map<number, Enrollment>>(new Map());
    const [catalogs, setCatalogs] = useState<CourseCatalogInfo[]>([]);
    const [assignmentDueDates, setAssignmentDueDates] = useState<Map<number, string>>(new Map());
    const [assignmentNotes, setAssignmentNotes] = useState<Map<number, string>>(new Map());
    const [courseRatings, setCourseRatings] = useState<Record<number, RatingSummary>>({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
    const [sort, setSort] = useState<SortKey>('name');
    const [view, setView] = useState<'grid' | 'list'>('grid');
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'info' | 'error' }>({ open: false, message: '', severity: 'success' });

    const dataProvider = useDataProvider();
    const notify = useNotify();
    const navigate = useNavigate();

    const { enroll: handleEnroll, enrollingCourseId } = useCourseEnrollment({
        notify: (message, severity) => setSnackbar({ open: true, message, severity }),
        onEnrolled: (courseId, enrollment) => {
            if (enrollment) {
                setEnrollments(prev => {
                    const updated = new Map(prev);
                    updated.set(courseId, enrollment as Enrollment);
                    return updated;
                });
            } else {
                loadData();
            }
        },
    });

    useEffect(() => {
        loadData();
    }, []);

    const getCompanyId = (): number | null => {
        const token = localStorage.getItem('token');
        if (!token) return null;
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.companyId ?? payload.company_id ?? null;
        } catch {
            return null;
        }
    };

    const getCourseId = (course: any): number => {
        if (typeof course === 'number') return course;
        if (course && typeof course === 'object' && course.id) return course.id;
        if (typeof course === 'string') {
            const parts = course.split('/');
            return parseInt(parts[parts.length - 1]);
        }
        return 0;
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const companyId = getCompanyId();
            const filter = buildCatalogCourseFilter(companyId);

            const [coursesResult, enrollmentsResult, catalogsResult] = await Promise.all([
                dataProvider.getList('courses', {
                    pagination: { page: 1, perPage: 200 },
                    sort: { field: 'title', order: 'ASC' },
                    filter
                }),
                dataProvider.getList('course_enrollments', {
                    pagination: { page: 1, perPage: 500 },
                    sort: { field: 'enrolledAt', order: 'DESC' },
                    filter: {}
                }),
                dataProvider.getList('course_catalogs', {
                    pagination: { page: 1, perPage: 100 },
                    sort: { field: 'name', order: 'ASC' },
                    filter: { isActive: true }
                }).catch(() => ({ data: [], total: 0 })),
            ]);

            const ownCourses = (coursesResult.data as any[]).map(c => ({
                ...c,
                catalogId: getCatalogId(c.catalog),
                catalogName: getCatalogName(c.catalog),
            })) as Course[];

            const enrollmentMap = new Map<number, Enrollment>();
            (enrollmentsResult.data as Enrollment[]).forEach(e => {
                const courseId = getCourseId(e.course);
                if (courseId) enrollmentMap.set(courseId, e);
            });
            setEnrollments(enrollmentMap);

            setCatalogs(((catalogsResult.data as any[]) || []).map(c => ({ id: c.id, name: c.name })));

            const ownCourseIds = new Set(ownCourses.map(c => c.id));
            let allCourses: Course[] = [...ownCourses];
            const token = localStorage.getItem('token');

            // Licensed courses (B2B marketplace) — bucketed under "Lizenzierte Kurse"
            try {
                const licensedResponse = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/licensed-courses`,
                    { headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } }
                );
                if (licensedResponse.ok) {
                    const licensedData = await licensedResponse.json();
                    const licensedCourses: Course[] = (licensedData.courses || [])
                        .filter((lc: any) =>
                            !ownCourseIds.has(lc.id) &&
                            lc.license?.isActiveForLicensee === true
                        )
                        .map((lc: any) => ({
                            id: lc.id,
                            title: lc.title,
                            description: lc.description,
                            thumbnailUrl: lc.thumbnailUrl,
                            estimatedDuration: lc.estimatedDuration,
                            difficulty: lc.difficulty,
                            isActive: true,
                            enrollmentMode: lc.license?.enrollmentModeForLicensee || 'none',
                            lessons: [],
                            isLicensedReference: true,
                            licenseId: lc.license?.id,
                            // Issue #275 Folge: HelloBetter-Kurse haben einen
                            // Catalog-Namen ("BARMER") aus dem Licensor-Tenant —
                            // das ist genau der Header, unter dem Walter sie in
                            // der Kursverwaltung sieht. Frueher hardcoded auf
                            // null, dadurch fielen alle Lizenzkurse in den
                            // "Lizenzierte Kurse"-Bucket.
                            catalogId: getCatalogId(lc.catalog),
                            catalogName: getCatalogName(lc.catalog),
                        }));
                    allCourses = [...allCourses, ...licensedCourses];
                }
            } catch {
                // non-critical
            }

            setCourses(allCourses);

            try {
                const courseIds = allCourses.map(c => c.id);
                if (courseIds.length > 0) {
                    const ratings = await ratingService.getRatingsBulk(courseIds);
                    setCourseRatings(ratings);
                }
            } catch {
                // non-critical
            }

            try {
                let assignUserId: number | null = null;
                if (token) {
                    try { assignUserId = JSON.parse(atob(token.split('.')[1])).userId ?? null; } catch {}
                }
                const userFilter = assignUserId ? `&userId=${assignUserId}` : '';
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/course_assignments?status[]=assigned&status[]=in_progress${userFilter}`,
                    { headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/ld+json' } }
                );
                if (response.ok) {
                    const data = await response.json();
                    const members: any[] = data['hydra:member'] ?? data['member'] ?? [];
                    const dueDateMap = new Map<number, string>();
                    const notesMap = new Map<number, string>();
                    members.forEach((a: any) => {
                        const cId = typeof a.course === 'object' ? a.course?.id : parseInt((a.course as string)?.split('/').pop() ?? '0');
                        if (cId && a.dueDate) dueDateMap.set(cId, a.dueDate);
                        if (cId && a.notes) notesMap.set(cId, a.notes);
                    });
                    setAssignmentDueDates(dueDateMap);
                    setAssignmentNotes(notesMap);
                }
            } catch {
                // non-critical
            }
        } catch (error) {
            notify('Fehler beim Laden des Kurskatalogs', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Alle Kurse mit Bucket (#275: Catalog-Header-Auswahl via pickCourseBucket),
    // Enrollment-Status, Rating und Pflichtaufgaben-Infos anreichern.
    const decorated = useMemo<CatalogCardData[]>(() =>
        courses.map(course => {
            const enrollment = enrollments.get(course.id);
            return {
                course,
                enrollment,
                bucket: pickCourseBucket(course, catalogs),
                status: getCatalogStatus(enrollment),
                ratingSummary: courseRatings[course.id],
                dueDate: assignmentDueDates.get(course.id),
                note: assignmentNotes.get(course.id),
            };
        }),
    [courses, enrollments, catalogs, courseRatings, assignmentDueDates, assignmentNotes]);

    // Katalog-Filteroptionen: aus den Buckets abgeleitet; "Lizenzierte Kurse"
    // und "Weitere Kurse" ans Ende (gleiche Reihenfolge wie frueher die Sections).
    const catalogOptions = useMemo<FilterOption[]>(() => {
        const seen = new Map<string, { value: string; label: string; key: number | string }>();
        decorated.forEach(d => {
            const k = String(d.bucket.key);
            if (!seen.has(k)) seen.set(k, { value: k, label: d.bucket.name, key: d.bucket.key });
        });
        return Array.from(seen.values())
            .sort((a, b) => {
                const aBottom = a.key === LICENSED_KEY || a.key === NO_CATALOG_KEY;
                const bBottom = b.key === LICENSED_KEY || b.key === NO_CATALOG_KEY;
                if (aBottom && !bBottom) return 1;
                if (!aBottom && bBottom) return -1;
                if (a.key === NO_CATALOG_KEY && b.key !== NO_CATALOG_KEY) return 1;
                if (b.key === NO_CATALOG_KEY && a.key !== NO_CATALOG_KEY) return -1;
                return a.label.localeCompare(b.label);
            })
            .map(({ value, label }) => ({ value, label }));
    }, [decorated]);

    const levelOptions = useMemo<FilterOption[]>(() => {
        const seen = new Set<string>();
        courses.forEach(c => { if (c.difficulty) seen.add(c.difficulty); });
        return Array.from(seen)
            .sort((a, b) =>
                ((difficultyOrder[a] ?? 99) - (difficultyOrder[b] ?? 99)) ||
                getDifficultyLabel(a).localeCompare(getDifficultyLabel(b))
            )
            .map(d => ({ value: d, label: getDifficultyLabel(d) }));
    }, [courses]);

    const visible = useMemo(() => {
        const search = searchTerm.toLowerCase().trim();
        const list = decorated.filter(d =>
            (!search ||
                d.course.title.toLowerCase().includes(search) ||
                (d.course.description || '').toLowerCase().includes(search)) &&
            (!filters.catalog.length || filters.catalog.includes(String(d.bucket.key))) &&
            (!filters.level.length || filters.level.includes(d.course.difficulty)) &&
            (!filters.status.length || filters.status.includes(d.status)) &&
            (!filters.enrollment.length ||
                filters.enrollment.includes(d.enrollment ? 'enrolled' : 'not_enrolled'))
        );
        return [...list].sort((a, b) => {
            if (sort === 'duration') {
                const diff = (a.course.estimatedDuration || 0) - (b.course.estimatedDuration || 0);
                if (diff !== 0) return diff;
            }
            if (sort === 'level') {
                const diff = (difficultyOrder[a.course.difficulty] ?? 99) - (difficultyOrder[b.course.difficulty] ?? 99);
                if (diff !== 0) return diff;
            }
            return a.course.title.localeCompare(b.course.title, 'de');
        });
    }, [decorated, searchTerm, filters, sort]);

    const toggleFilter = (group: FilterGroupKey, value: string) => {
        setFilters(prev => ({
            ...prev,
            [group]: prev[group].includes(value)
                ? prev[group].filter(v => v !== value)
                : [...prev[group], value],
        }));
    };

    const resetFilters = () => {
        setFilters(EMPTY_FILTERS);
        setSearchTerm('');
    };

    const optionLabel = (group: FilterGroupKey, value: string): string => {
        const options =
            group === 'catalog' ? catalogOptions :
            group === 'level' ? levelOptions :
            group === 'status' ? STATUS_OPTIONS : ENROLLMENT_OPTIONS;
        return options.find(o => o.value === value)?.label ?? value;
    };

    const activeChips = (['catalog', 'level', 'status', 'enrollment'] as FilterGroupKey[])
        .flatMap(group => filters[group].map(value => ({ group, value, label: optionLabel(group, value) })));

    const hasActiveFilters = activeChips.length > 0 || searchTerm.trim() !== '';

    const handleContinue = (courseId: number) => navigate(`/course-player/${courseId}`);

    if (loading) {
        return (
            <div className="oa-catalog">
                <p className="cat-loading">Kurskatalog wird geladen...</p>
            </div>
        );
    }

    return (
        <div className="oa-catalog">
            <div className="cat2">
                <aside className="cat2-side">
                    {catalogOptions.length > 0 && (
                        <FilterGroup
                            title="Katalog"
                            options={catalogOptions}
                            selected={filters.catalog}
                            onToggle={(v) => toggleFilter('catalog', v)}
                        />
                    )}
                    {levelOptions.length > 0 && (
                        <FilterGroup
                            title="Level"
                            options={levelOptions}
                            selected={filters.level}
                            onToggle={(v) => toggleFilter('level', v)}
                        />
                    )}
                    <FilterGroup
                        title="Status"
                        options={STATUS_OPTIONS}
                        selected={filters.status}
                        onToggle={(v) => toggleFilter('status', v)}
                    />
                    <FilterGroup
                        title="Einschreibung"
                        options={ENROLLMENT_OPTIONS}
                        selected={filters.enrollment}
                        onToggle={(v) => toggleFilter('enrollment', v)}
                    />
                    <button type="button" className="fil-reset" onClick={resetFilters}>
                        Filter zurücksetzen
                    </button>
                </aside>

                <section className="cat2-main">
                    <div className="cat2-toolbar">
                        <div className="cat2-search">
                            <MaterialSymbol name="search" size={18} />
                            <input
                                type="text"
                                placeholder="Kurse suchen..."
                                aria-label="Kurse suchen"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="cat2-tools">
                            <select
                                className="catsort"
                                aria-label="Sortierung"
                                value={sort}
                                onChange={(e) => setSort(e.target.value as SortKey)}
                            >
                                <option value="name">Name (A–Z)</option>
                                <option value="duration">Dauer</option>
                                <option value="level">Level</option>
                            </select>
                            <div className="viewtoggle">
                                <button
                                    type="button"
                                    className={view === 'grid' ? 'active' : ''}
                                    aria-label="Raster"
                                    onClick={() => setView('grid')}
                                >
                                    <MaterialSymbol name="dashboard" size={18} />
                                </button>
                                <button
                                    type="button"
                                    className={view === 'list' ? 'active' : ''}
                                    aria-label="Liste"
                                    onClick={() => setView('list')}
                                >
                                    <MaterialSymbol name="format_list_bulleted" size={18} />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="cat2-meta">
                        <span className="cat-count">
                            {visible.length} {visible.length === 1 ? 'Kurs' : 'Kurse'}
                        </span>
                        {activeChips.length > 0 && (
                            <div className="chips">
                                {activeChips.map((chip) => (
                                    <button
                                        key={`${chip.group}-${chip.value}`}
                                        type="button"
                                        className="ac"
                                        onClick={() => toggleFilter(chip.group, chip.value)}
                                    >
                                        {chip.label}
                                        <MaterialSymbol name="close" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {visible.length === 0 ? (
                        <p className="cat2-empty">
                            {hasActiveFilters
                                ? 'Keine Kurse entsprechen den Filtern.'
                                : 'Keine Kurse im Katalog verfügbar.'}
                        </p>
                    ) : (
                        <div className={`cat2-grid${view === 'list' ? ' list' : ''}`}>
                            {visible.map((data) => (
                                <CatalogCard
                                    key={data.course.id}
                                    data={data}
                                    onEnroll={handleEnroll}
                                    enrolling={enrollingCourseId === data.course.id}
                                    onContinue={handleContinue}
                                />
                            ))}
                        </div>
                    )}
                </section>
            </div>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default CourseCatalog;
